import { useState } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiPost } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

interface LoyaltyProfileProps {
  showAvatar?: boolean;
  showMemberSince?: boolean;
  showEditButton?: boolean;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#128100;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view your profile
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to manage your account settings.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

export function LoyaltyProfileBlock({ props }: { props: LoyaltyProfileProps }) {
  const { isAuthenticated, user } = useAuth();
  const { data, loading } = useLoyalty();
  const showAvatar = props.showAvatar ?? true;
  const showMemberSince = props.showMemberSince ?? true;

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[var(--color-surface-border)]" />
          <div className="space-y-2 flex-1">
            <div className="h-5 w-40 bg-[var(--color-surface-border)] rounded" />
            <div className="h-4 w-24 bg-[var(--color-surface-border)] rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!data || !isAuthenticated) return <LoginCta />;

  const profile = data.profile;
  const initials = `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  function startEdit() {
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setEditing(true);
    setToast(null);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiPost('/customer/profile', { firstName, lastName });
      setToast('Profile updated!');
      setEditing(false);
      // Could refetch loyalty data here
    } catch {
      setToast('Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="loyalty-card p-6">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {showAvatar && (
          <div className="flex-shrink-0">
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={`${profile.firstName} ${profile.lastName}`}
                className="w-16 h-16 rounded-full object-cover border-2 border-[var(--color-primary)]"
              />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold bg-[var(--color-primary)] text-white">
                {initials}
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-[var(--color-surface-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-[var(--color-surface-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="loyalty-heading text-lg text-[var(--color-text)]">
                {profile.firstName} {profile.lastName}
              </h3>
              {profile.tierName && (
                <span
                  className="inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white mt-1"
                  style={{ backgroundColor: profile.tierColor ?? 'var(--color-primary)' }}
                >
                  {profile.tierName}
                </span>
              )}
              {showMemberSince && profile.enrolledAt && (
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  Member since {new Date(profile.enrolledAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Edit button */}
        {!editing && (
          <button
            onClick={startEdit}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-[var(--color-surface-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text)] transition-colors"
          >
            Edit
          </button>
        )}
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-[var(--color-surface-border)]">
        <div className="text-center">
          <div className="text-lg font-bold text-[var(--color-primary)]">
            {profile.pointsBalance.toLocaleString()}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">Points</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-[var(--color-text)]">
            {profile.lifetimePoints.toLocaleString()}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">Lifetime</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-[var(--color-text)]">
            {data.stats.unlockedBadges}/{data.stats.totalBadges}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">Badges</div>
        </div>
      </div>

      {toast && (
        <div className="mt-3 p-2 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          {toast}
        </div>
      )}
    </div>
  );
}
